<template>
  <div class="row-root" :class="{ private: isPrivateToMe }">
    <div class="row-main">
      <div class="icon-well" aria-hidden="true">{{ itemIconForType(pile.item?.type) }}</div>
      <div class="content">
        <div class="topline">
          <div class="name">{{ pile.itemName }}</div>
          <UiChip v-if="isPrivateToMe" variant="muted">Private</UiChip>
        </div>
        <div class="subline text-muted">
          <span class="stat-mono num">×{{ formatNumber(pile.qty ?? 0) }}</span>
          <span class="dot">•</span>
          <span class="stat-mono num">{{ markForPile(pile) }}</span>
          <span class="dot">•</span>
          <span class="stat-mono num">{{ formatNumber(totalValue) }}g</span>
          <span v-if="pile.referencePrice" class="text-muted">({{ pile.referencePrice.source }})</span>
        </div>
      </div>
    </div>

    <div class="row-actions">
      <UiButton size="sm" variant="secondary" :disabled="disabled" @click="emit('take', pile.id)">
        Take
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { GroundLootPile } from "@idle-rpg/shared";
import { formatNumber } from "../../../lib/format";
import { getItemMarkDisplay } from "../../../lib/itemMarks";
import { itemIconForType } from "../../../lib/itemIcons";
import { useItemMarkMode } from "../../../composables/useItemMarkMode";
import UiButton from "../../ui/UiButton.vue";
import UiChip from "../../ui/UiChip.vue";

const props = defineProps<{
  pile: GroundLootPile;
  disabled?: boolean;
  isPrivateToMe: boolean;
}>();

const emit = defineEmits<{
  take: [pileId: string];
}>();

const { mode: itemMarkMode } = useItemMarkMode();

function markForPile(pile: GroundLootPile): string {
  return getItemMarkDisplay(itemMarkMode.value, pile.itemId);
}

const totalValue = computed(() => {
  const unit = props.pile.referencePrice?.price ?? props.pile.item?.basePrice ?? 0;
  return Math.max(0, unit * (props.pile.qty ?? 0));
});
</script>

<style scoped>
.row-root {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.75rem 0.8rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
}

.row-root.private {
  border-color: color-mix(in srgb, var(--accent-travel) 30%, var(--glass-border-0));
  background: color-mix(in srgb, var(--accent-travel) 8%, var(--glass-0));
}

.row-main {
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
  min-width: 0;
}

.icon-well {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
  flex: 0 0 auto;
}

.content {
  min-width: 0;
  display: grid;
  gap: 0.25rem;
}

.topline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.name {
  font-weight: 800;
}

.subline {
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.dot {
  opacity: 0.65;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}
</style>
